import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { POST } from "@/constants/testIds";

export const FINANCIER_IDS = {
  topupDialog: "financier-topup-dialog",
  topupConfirm: "financier-topup-confirm-button",
  topupShortfall: "financier-topup-shortfall-amount",
};

export default function FinancierPanel({ postId, onDone }) {
  const [voterPct, setVoterPct] = useState(50);
  const [amount, setAmount] = useState(500); // 5 CAD cents
  const [busy, setBusy] = useState(false);
  const [shortfall, setShortfall] = useState(null);
  const authorPct = 100 - voterPct;

  const pledge = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${postId}/financier`, { voter_pct: voterPct, amount_cents: amount });
      toast.success(`Pledged ${(amount / 100).toFixed(2)} CAD from wallet`);
      onDone?.();
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 402 && data?.detail === "insufficient_wallet_balance") {
        setShortfall({
          needed_cents: data.needed_cents,
          available_cents: data.available_cents,
          shortfall_cents: data.shortfall_cents,
        });
      } else {
        toast.error(data?.detail || "Pledge failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const topupAndPledge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/financier/topup-and-pledge`, {
        voter_pct: voterPct,
        amount_cents: amount,
        topup_cents: shortfall.shortfall_cents,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-4 p-4 rounded bg-black/40 border border-white/10">
        <p className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2">
          Financier split (never outside 20/80 – 80/20)
        </p>
        <Slider
          data-testid={POST.financierSlider}
          value={[voterPct]}
          min={20}
          max={80}
          step={1}
          onValueChange={(v) => setVoterPct(v[0])}
          className="my-3"
        />
        <div data-testid={POST.financierSplitIndicator} className="flex justify-between text-xs font-mono text-slate-300">
          <span>Voter Faculty <b className="text-emerald-400">{voterPct}%</b></span>
          <span>Author <b className="text-yellow-400">{authorPct}%</b></span>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Amount CAD</label>
          <input
            type="number"
            min="1"
            max="1000"
            step="0.5"
            value={(amount / 100).toFixed(2)}
            onChange={(e) => setAmount(Math.round(parseFloat(e.target.value || "0") * 100))}
            className="flex-1 bg-[#12141c] border border-[#222634] rounded px-2 py-1 text-sm text-slate-100 font-mono"
          />
          <Button
            onClick={pledge}
            disabled={busy}
            size="sm"
            className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full"
          >
            {busy ? "Pledging…" : "Pledge from wallet"}
          </Button>
        </div>
        <p className="mt-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">
          Deducted from your CAI wallet. If short, you&apos;ll be offered a card top-up.
        </p>
      </div>

      <Dialog
        open={shortfall !== null}
        onOpenChange={(v) => !v && setShortfall(null)}
      >
        <DialogContent data-testid={FINANCIER_IDS.topupDialog} className="bg-[#12141c] border-[#222634] text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle>Insufficient wallet balance</DialogTitle>
            <DialogDescription className="text-slate-400">
              You&apos;re {" "}
              <span data-testid={FINANCIER_IDS.topupShortfall} className="font-mono text-rose-300">
                ${((shortfall?.shortfall_cents ?? 0) / 100).toFixed(2)}
              </span>{" "}
              short of covering this pledge. Top up by that amount and the pledge
              will execute automatically after payment.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs font-mono text-slate-400 space-y-1 py-2">
            <p>Pledge total: <span className="text-slate-200">${((shortfall?.needed_cents ?? 0) / 100).toFixed(2)}</span></p>
            <p>Wallet balance: <span className="text-slate-200">${((shortfall?.available_cents ?? 0) / 100).toFixed(2)}</span></p>
            <p>Shortfall · charged to card: <span className="text-rose-300">${((shortfall?.shortfall_cents ?? 0) / 100).toFixed(2)}</span></p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShortfall(null)} className="text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid={FINANCIER_IDS.topupConfirm}
              onClick={topupAndPledge}
              disabled={busy}
              className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold"
            >
              {busy ? "Opening Stripe…" : "Top up & pledge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
